import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { AREAS, LEVELS } from "@/lib/heat";
import { clearInsightsCache } from "@/lib/useInsights";

const GROUPS = [...AREAS.map((a) => ({ key: a.key, label: a.label })), { key: "overall", label: "Overall" }];

export const InsightsEditor = () => {
  const [ins, setIns] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/public/insights").then((r) => setIns(r.data.insights)).catch(() => {});
  }, []);

  if (!ins) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/insights", { insights: ins });
      setIns(data.insights);
      clearInsightsCache();
      toast.success("Report messages saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="insights-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Report messages</h2>
          <p className="mt-1 text-sm text-slate-500">Shown on every report for the level each petal (and the overall average) reaches.</p>
        </div>
        <Button data-testid="save-insights-button" onClick={save} disabled={busy} className="rounded-full bg-[#1F4266] hover:bg-[#122A43]">
          <Save className="mr-2 h-4 w-4" /> Save messages
        </Button>
      </div>
      <Tabs defaultValue="body" className="mt-5">
        <TabsList className="flex flex-wrap h-auto">
          {GROUPS.map((g) => (
            <TabsTrigger key={g.key} value={g.key} data-testid={`insights-tab-${g.key}`}>{g.label}</TabsTrigger>
          ))}
        </TabsList>
        {GROUPS.map((g) => (
          <TabsContent key={g.key} value={g.key} className="space-y-4 pt-2">
            {LEVELS.map((lv) => (
              <div key={lv.label}>
                <div className="mb-1 flex items-baseline gap-2 text-sm">
                  <span className="font-semibold" style={{ color: lv.hex }}>{lv.label}</span>
                  <span className="font-mono text-xs text-slate-400">
                    {g.key === "overall" ? `average ${lv.min}–${lv.max} per area` : `${lv.min}–${lv.max} pts`}
                  </span>
                </div>
                <Textarea
                  data-testid={`insight-${g.key}-${lv.label.toLowerCase().replace(/\s+/g, "-")}`}
                  rows={3}
                  value={ins[g.key]?.[lv.label] || ""}
                  onChange={(e) => setIns({ ...ins, [g.key]: { ...ins[g.key], [lv.label]: e.target.value } })}
                />
              </div>
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
};
